import { Health } from "../graphql/graphql";
import type { ColumnFiltersState } from "@tanstack/react-table";

/**
 * Maps an exception status string to a valid Tag color.
 * @param status The status string (e.g. "OPEN", "RESOLVED", etc.)
 * @returns A Tag color ("teal" | "blue" | "green" | "yellow" | "pink" | "orange" | "purple")
 */
export function mapStatusToColor(
  status: string
): "teal" | "blue" | "green" | "yellow" | "pink" | "orange" | "purple" {
  if (!status) return "yellow";
  const normalized = status.toUpperCase();
  if (normalized === "OPEN") return "blue";
  if (normalized === "RESOLVED") return "green";
  // Add more mappings as needed
  return "yellow"; // default/fallback
}

/**
 * Maps a priority string to a status for the Tag component.
 * @param priority The priority string (e.g. "URGENT", "HIGH", etc.)
 * @returns A status string ("danger", "warning", "neutral" or "inactive")
 */
export function mapPriorityToStatus(priority: string): 'danger' | 'warning' | 'neutral' | 'inactive' {
  switch (priority?.toUpperCase()) {
    case 'URGENT':
      return 'danger';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'neutral';
    case 'LOW':
      return 'inactive';
    default:
      return 'neutral';
  }
}

export function formatDate(dateString: string, toLocal: boolean = false, showTimezone: boolean = false) {
  if (!dateString) return "";

  const date = new Date(dateString);
  let formatted = date.toLocaleDateString(navigator.language);
  return formatted;
}

export function formatDateTime(dateString: string, toLocal: boolean = false, showTimezone: boolean = false) {
  if (!dateString) return "";

  const date = new Date(dateString);
  let formatted = date.toLocaleString(navigator.language);
  return formatted;
}

// Utility to calculate urgency based on plannedDischargeDate
export function getUrgencyTag(exception: { targetResolutionDate?: string }): { color: "teal" | "blue" | "green" | "yellow" | "pink" | "orange" | "purple"; text: string } {
  if (!exception?.targetResolutionDate) {
    return { color: "teal", text: "N/A" };
  }
  const targetDate = new Date(exception.targetResolutionDate);
  const today = new Date();
  targetDate.setHours(0,0,0,0);
  today.setHours(0,0,0,0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const daysDiff = Math.round((targetDate.getTime() - today.getTime()) / msPerDay);

  let color: "pink" | "yellow" | "green" | "teal" = "green";
  if (daysDiff <= 7) color = "pink"; // Urgent
  else if (daysDiff <= 10) color = "yellow"; // High Priority
  else if (daysDiff > 10) color = "green"; // Normal

  const text = `ETA ${daysDiff >= 0 ? "-" : "+"}${Math.abs(daysDiff)}`;
  return { color, text };
}

// Utility to format a Date as AWSDate (YYYY-MM-DD)
export function toAWSDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

// Generate urgency options: ETA -3, ETA -2, ..., ETA -0, ETA +1, ..., ETA +14
export function generateUrgencyOptions(rangePast = 7, rangeFuture = 14): { value: string; label: string }[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const options: { value: string; label: string }[] = [];

  for (let i = -rangePast; i <= rangeFuture; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    const value = toAWSDate(date);
    const label = `ETA ${i >= 0 ? '+' : ''}${i}`;
    options.push({ value, label });
  }
  return options;
}

/**
 * Returns the current step from an array of steps.
 * The current step is the one where completedTimestamp is null.
 * @param steps Array of step objects
 * @returns The current step object, or undefined if none found
 */
export function getCurrentStep<T extends { completedTimestamp?: string | null }>(steps: T[]): T | undefined {
  return steps.find(step => step.completedTimestamp == null);
}

export function mapHealthStatusToIndicator(health: Health | null | undefined): "success" | "warning" | "danger" | "neutral" | "inactive" {
  switch (health) {
    case Health.Healthy:
      return "success";
    case Health.Warning:
      return "warning";
    case Health.Error:
      return "danger";
    default:
      return "neutral";
  }
}

export function encodeFilters(filters: ColumnFiltersState): string {
    return filters
      .filter(f => f.value !== undefined && f.value !== null && f.value !== '')
      .map(f => {
        if (
          typeof f.value === 'object' &&
          f.value !== null &&
          ('from' in f.value || 'to' in f.value)
        ) {
          const from = (f.value as { from?: string }).from || '';
          const to = (f.value as { to?: string }).to || '';
          return `${encodeURIComponent(f.id)}:${encodeURIComponent(from)},${encodeURIComponent(to)}`;
        }
        return `${encodeURIComponent(f.id)}:${encodeURIComponent(String(f.value))}`;
      })
      .join(';');
  }
  // Decode filters from key:value;key:value (or key:from,to) format
export function decodeFilters(encoded: string | null): ColumnFiltersState {
    if (!encoded) return [];
    try {
      return encoded.split(';').map(pair => {
        const [key, val] = pair.split(':');
        if (val && val.includes(',')) {
          // dateRange
          const [from, to] = val.split(',');
          if (!from && !to) return null;
          return { id: decodeURIComponent(key), value: { from: decodeURIComponent(from), to: decodeURIComponent(to) } };
        }
        return { id: decodeURIComponent(key), value: decodeURIComponent(val) };
      }).filter(Boolean) as ColumnFiltersState;
    } catch {
      return [];
    }
  }
